import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import * as UserAPI from "../services/api/user";
import type { ModulePermissions } from "../services/api/user/types";
import { useAuth } from "./AuthContext";
import {
  applyDeploymentRoutingTierFromModules,
  clearDeploymentRoutingState,
} from "@/utils/deploymentRouting";
import { hasModulePermission, hasModuleMembership } from "@/utils/permissionUtils";
import { isCompanyAllowedForModule } from "@/utils/moduleAvailability";
import { localDevBypassesModule } from "@/utils/localDevModuleBypass";

interface CompanyLayoutSettings {
  forceAniOs: boolean;
  disableClassicMode: boolean;
}

interface ModulePermissionsContextType {
  permissions: ModulePermissions | null;
  isLoading: boolean;
  error: string | null;
  /** Effective access check (honours `*` / `admin:*` wildcards). Use for route/API gates. */
  hasPermission: (module: string) => boolean;
  /**
   * Strict membership check (ignores wildcards). Use for UI surfaces that should
   * reflect a user's *configured* modules, e.g. the aniOS dock and Apps popover —
   * admins still see only the apps explicitly assigned to them.
   */
  hasModule: (module: string) => boolean;
  companyLayoutSettings: CompanyLayoutSettings;
}

const defaultCompanyLayoutSettings: CompanyLayoutSettings = {
  forceAniOs: false,
  disableClassicMode: false,
};

const ModulePermissionsContext = createContext<ModulePermissionsContextType>({
  permissions: null,
  isLoading: true,
  error: null,
  hasPermission: () => false,
  hasModule: () => false,
  companyLayoutSettings: defaultCompanyLayoutSettings,
});

export function ModulePermissionsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [permissions, setPermissions] = useState<ModulePermissions | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const permissionsRef = useRef<ModulePermissions | null>(null);
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();

  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated) {
      clearDeploymentRoutingState();
    }
  }, [isAuthenticated, isAuthLoading]);

  useEffect(() => {
    const fetchPermissions = async () => {
      // Don't fetch if auth is still loading or not authenticated
      if (isAuthLoading || !isAuthenticated) {
        return;
      }

      try {
        const data = await UserAPI.getModulePermissions();
        applyDeploymentRoutingTierFromModules(data);
        if (JSON.stringify(data) !== JSON.stringify(permissionsRef.current)) {
          permissionsRef.current = data;
          setPermissions(data);
        }
      } catch (err) {
        console.error("Error fetching permissions:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load permissions"
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchPermissions();
  }, [isAuthenticated, isAuthLoading]);

  const hasPermission = useCallback(
    (module: string): boolean => {
      if (localDevBypassesModule(module)) {
        return true;
      }

      if (!permissions) {
        return false;
      }

      const normalizedModule = module.toLowerCase().replace(/-/g, "_");
      if (!isCompanyAllowedForModule(user?.company_id, normalizedModule)) {
        return false;
      }

      return hasModulePermission(module, permissions);
    },
    [permissions, user?.company_id],
  );

  const hasModule = useCallback(
    (module: string): boolean => {
      if (localDevBypassesModule(module)) {
        return true;
      }
      return hasModuleMembership(module, permissions);
    },
    [permissions],
  );

  const companyLayoutSettings = useMemo<CompanyLayoutSettings>(() => {
    if (!permissions) {
      return defaultCompanyLayoutSettings;
    }
    return {
      forceAniOs: permissions.force_ani_os ?? false,
      disableClassicMode: permissions.disable_classic_mode ?? false,
    };
  }, [permissions]);

  return (
    <ModulePermissionsContext.Provider
      value={{
        permissions,
        isLoading: isLoading || isAuthLoading,
        error,
        hasPermission,
        hasModule,
        companyLayoutSettings,
      }}
    >
      {children}
    </ModulePermissionsContext.Provider>
  );
}

export function useModulePermissions() {
  return useContext(ModulePermissionsContext);
}
